import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Clock, Footprints, Shirt, Flower2, Loader2, Send, Phone, Mail } from "lucide-react";

const ETIQUETTE = [
  { icon: Footprints, text: "Please remove shoes before entering the sanctum" },
  { icon: Shirt, text: "Modest, comfortable clothing is appreciated" },
  { icon: Flower2, text: "Flowers and fruit offerings are welcome; prasad is shared with all" },
];

const SEVA = ["Cooking and serving the Saturday bhandara", "Making fresh flower garlands for Baba", "Leading or playing kirtan during aarti", "Temple cleaning and garden care"];

export default function VisitAndSeva() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Your message has reached the temple. Jai Shri Ram!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="visit" data-testid="visit-section" className="py-20 sm:py-28 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow mb-4">10 · Come Home to Baba</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-950" data-testid="visit-heading">
            Visit the Mandir &amp; Offer Seva
          </h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl border border-amber-900/10 p-7" data-testid="visit-address-card">
              <div className="flex items-start gap-4">
                <MapPin className="h-6 w-6 text-orange-700 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-stone-900">Neem Karoli Baba Temple</h3>
                  <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                    1642 FM 1310<br />Denison, Texas 75020<br />Texoma region, North Texas
                  </p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=1642+FM+1310+Denison+TX+75020"
                    target="_blank" rel="noopener noreferrer"
                    data-testid="directions-link"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800 hover:text-orange-700 transition-colors"
                  >
                    Get directions on Google Maps →
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-amber-900/10 p-7" data-testid="visit-contact-card">
              <h3 className="font-semibold text-stone-900 mb-4">Talk to the Temple</h3>
              <div className="space-y-3 text-sm">
                <a href="tel:+12143956757" data-testid="contact-phone-link"
                  className="flex items-center gap-3 text-stone-600 hover:text-amber-800 transition-colors">
                  <Phone className="h-4 w-4 text-amber-700 shrink-0" /> (214) 395-6757
                </a>
                <a href="mailto:Poorvesh.thakkar@gmail.com" data-testid="contact-email-link"
                  className="flex items-center gap-3 text-stone-600 hover:text-amber-800 transition-colors break-all">
                  <Mail className="h-4 w-4 text-amber-700 shrink-0" /> Poorvesh.thakkar@gmail.com
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-amber-900/10 p-7" data-testid="visit-hours-card">
              <div className="flex items-start gap-4">
                <Clock className="h-6 w-6 text-orange-700 shrink-0 mt-0.5" />
                <div className="w-full">
                  <h3 className="font-semibold text-stone-900 mb-3">Darshan Hours — Every Saturday</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between border-b border-amber-900/5 pb-2">
                      <span className="text-stone-600">Morning Darshan</span>
                      <span className="font-medium text-amber-900">8:30 – 11:30 AM</span>
                    </div>
                    <div className="flex justify-between border-b border-amber-900/5 pb-2">
                      <span className="text-stone-600">Evening Aarti</span>
                      <span className="font-medium text-amber-900">5:00 – 8:30 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Satsang &amp; Bhandara</span>
                      <span className="font-medium text-amber-900">Saturdays, after Aarti</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-amber-900/10 p-7" data-testid="visit-etiquette-card">
              <h3 className="font-semibold text-stone-900 mb-4">Temple Etiquette</h3>
              <ul className="space-y-3">
                {ETIQUETTE.map((e) => (
                  <li key={e.text} className="flex items-center gap-3 text-sm text-stone-600">
                    <e.icon className="h-5 w-5 text-amber-700 shrink-0" /> {e.text}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl border border-amber-900/10 p-7" data-testid="seva-card">
              <h3 className="font-semibold text-amber-950 mb-1.5">Seva — Serve With Us</h3>
              <p className="text-sm text-amber-900/70 mb-4">Baba taught that service is worship. Volunteers are the heartbeat of this mandir.</p>
              <ul className="space-y-2">
                {SEVA.map((s) => (
                  <li key={s} className="flex items-start gap-2.5 text-sm text-amber-950/85">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-600 shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
            className="bg-white rounded-3xl border border-amber-900/10 p-7 sm:p-9 shadow-xl shadow-amber-900/5 h-fit"
            data-testid="contact-form-card"
          >
            <h3 className="font-display text-xl text-amber-950 mb-2">Write to the Temple</h3>
            <p className="text-sm text-stone-500 mb-6">
              Questions about visiting, seva, sponsoring a bhandara, or the new temple project? Send us a message.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Your name</label>
                <Input data-testid="contact-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-amber-900/15 focus-visible:ring-amber-600" placeholder="Full name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Email</label>
                <Input data-testid="contact-email-input" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-amber-900/15 focus-visible:ring-amber-600" placeholder="you@example.com" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Message</label>
                <Textarea data-testid="contact-message-input" required value={form.message} rows={5}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1.5 rounded-xl border-amber-900/15 focus-visible:ring-amber-600 resize-none"
                  placeholder="How can we help?" />
              </div>
              <Button data-testid="contact-submit-button" type="submit" disabled={sending}
                className="w-full h-12 rounded-full bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold shadow-lg shadow-amber-900/20 transition-all duration-300">
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Send className="h-4 w-4 mr-2" /> Send Message</>)}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
